import { z } from 'zod';
import { TenantIdSchema, TimestampSchema } from '../types/common.types';
import { PublicStockStatusSchema } from './stock.schema';

/**
 * Integrációs esemény-katalógus — az rPI ERP **kifelé** publikált eseményei.
 *
 * ADR-145 (`D` üzleti modell, Javo 2026-09-08): az rPI Web önállóan is eladható
 * termék, ami **bármelyik ERP-hez** csatlakozik. Az ígéret hordozója ez a katalógus:
 * egy idegen ERP **ezt** teljesíti, nem a ~636 belső végpontot.
 *
 * ## Ami ide tartozik, és ami nem
 *
 * Ez a fájl a **külső alak**. A belső modellek (`shared/inventory`, Prisma-enumok)
 * ide **nem re-exportálódnak** — a leképezés explicit mapper a publikáló oldalon.
 * Az `export *` itt nem stílus-kérdés lenne, hanem **adatszivárgás**: egy belső
 * enum-bővítés (pl. `InventoryStatus.STOLEN`) némán kikerülne a publikus API-ra.
 *
 * ## Névkonvenció — `<fogalom>.<múlt idejű ige>`, snake_case
 *
 * A repo egyetlen élő kifelé-alakját követi (`${entityType}.status_changed`,
 * `workflow-integration.service.ts:280`), **nem** az outbox SCREAMING literáljait
 * (`NAV_INVOICE`, `JOURNAL_POSTING`) — azok belsők.
 *
 * ⛔ `partner.upserted` **nincs**: az upsert a mi implementációs részletünk, egy
 * idegen ERP a létrehozást és a módosítást külön látja.
 *
 * ## Verziózás — SOHA a névben
 *
 * A `schemaVersion` a payloadban van. A `WebhookRegistration.events[]` a
 * feliratkozás kulcsa: névbeli verzió (`partner.created.v2`) minden bumpnál törné a
 * meglévő feliratkozásokat, és a `*` wildcard (`webhook-emitter.service.ts:341`)
 * is értelmét vesztené.
 *
 * ## Idempotencia
 *
 * A kézbesítés **at-least-once** (outbox + retry), ezért a dedup a fogyasztó dolga,
 * és meg kell hozzá kapnia a kulcsot: `id` (esemény-azonosító) + `aggregate`.
 * Ez utóbbi az outbox `@@unique([aggregateType, aggregateId, eventType])` hármasának
 * kifelé látható fele.
 *
 * @see ADR-145 — Outbox ERP-szintre, outbox→webhook híd, két auth-osztály
 */

/** A katalógus verziója. Bump: additív mezőnél minor, törő változásnál major. */
export const INTEGRATION_EVENT_SCHEMA_VERSION = '1.0' as const;

/**
 * A publikált esemény-nevek zárt készlete.
 *
 * A `4` fogalom (ADR-145 A4): partner · cikk · készlet · rendelés/foglalás.
 * A készlet és a foglalás **állapotváltásként** publikál (`*.changed` /
 * `*.status_changed`), nem állapotonként külön eseményként — a `RentalStatus`
 * `8` értéke különben `8` eseményt szülne, és minden új állapot törné a katalógust.
 */
export const IntegrationEventNameSchema = z.enum([
  'partner.created',
  'partner.updated',
  'product.created',
  'product.updated',
  'stock.changed',
  'rental.created',
  'rental.status_changed',
]);
export type IntegrationEventName = z.infer<typeof IntegrationEventNameSchema>;

/**
 * Bérlés-állapot **kifelé** — a `RentalStatus` `8` értékéből `7`.
 *
 * Javo-döntés (2026-09-08, tty): *„a belsők ne menjenek, a `partially returned`
 * mehet"*. A `DRAFT` az egyetlen kiszűrt: a piszkozat nem tartozik a vevőre.
 * Az `OVERDUE` **megy** — a bérlőnek tudnia kell, hogy késett.
 */
export const PublicRentalStatusSchema = z.enum([
  'CONFIRMED',
  'ACTIVE',
  'OVERDUE',
  'PARTIALLY_RETURNED',
  'RETURNED',
  'COMPLETED',
  'CANCELLED',
]);
export type PublicRentalStatus = z.infer<typeof PublicRentalStatusSchema>;

/** Az aggregátum, amire az esemény vonatkozik — a fogyasztói dedup kulcsának fele. */
export const EventAggregateSchema = z.object({
  type: z.enum(['PARTNER', 'PRODUCT', 'STOCK', 'RENTAL']),
  id: z.string().uuid(),
});
export type EventAggregate = z.infer<typeof EventAggregateSchema>;

/**
 * A közös boríték. Minden publikált esemény ezt viseli.
 *
 * ⚠️ A `tenantId` **benne van**, és ez szándékos: a fogyasztó több tenant
 * eseményeit is kaphatja egy végponton (franchise-központ), és a szétválasztás
 * nem a kézbesítési URL-en múlhat.
 */
export const IntegrationEventEnvelopeSchema = z.object({
  /** Esemény-azonosító — a fogyasztói dedup elsődleges kulcsa (at-least-once). */
  id: z.string().uuid(),
  schemaVersion: z.string().min(1),
  event: IntegrationEventNameSchema,
  tenantId: TenantIdSchema,
  aggregate: EventAggregateSchema,
  occurredAt: TimestampSchema,
  /**
   * Az ELSO kezbesitesi kiserlet ideje (`ADR-145` `Am.3` `E2/a`).
   *
   * Opcionalis, mert a mai emitter meg nem toltir ki — de a **semaban benne kell lennie**,
   * mielott barmelyik oldal `.parse()`-ol a boritekra. Kulonben a mezo nemant kiesik a
   * torzsbol ES az alairt anyagbol is (🛡️ lelete, 2026-09-09): az emit sikeres, az alairas
   * ervenyes, es a fogado `4xx stale-event` againak nincs mit neznie — pont az a vedelem
   * tunik el, amit az `Am.3` a boritekba (nem fejlecbe) tett.
   *
   * ⛔ NEM azonos az `occurredAt`-tal: az az uzleti esemeny ideje, ez a kezbesites-kiserlete.
   * A ketto osszemosasa az `E4` nema hibaja.
   */
  firstAttemptAt: TimestampSchema.optional(),
});
export type IntegrationEventEnvelope = z.infer<typeof IntegrationEventEnvelopeSchema>;

/**
 * Partner — a **kifelé** látható törzsadat-részhalmaz.
 *
 * ⛔ Szándékosan **nincs** benne: `taxNumber` · `birthDate` · `mothersName` ·
 * `idCardNumber` · `drivingLicenseNo` · `temporaryAddress`. Ezek a `Partner`
 * modellen léteznek, de személyazonosító adatok — egy integrációs eseménybe nem
 * valók. Aki ilyet igényel, az külön, auditált végponton kérje.
 */
export const PartnerEventPayloadSchema = z.object({
  partnerId: z.string().uuid(),
  /**
   * ⛔ SZÁNDÉKOSAN saját literál, nem a `PartnerTypeSchema` újrahasznosítása: a wire-alak
   * nem bővülhet némán attól, hogy a REST-szerződés bővül. A két helynek EGYEZNIE kell,
   * és a földi igazság a Prisma `enum PartnerType` (`2` érték) — l. partner.schema.ts.
   */
  type: z.enum(['INDIVIDUAL', 'COMPANY']),
  name: z.string().min(1).max(255),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  isActive: z.boolean(),
});
export type PartnerEventPayload = z.infer<typeof PartnerEventPayloadSchema>;

/** Cikk — a kifelé látható törzsadat. A beszerzési ár és a marzs **nem** megy ki. */
export const ProductEventPayloadSchema = z.object({
  productId: z.string().uuid(),
  sku: z.string().min(1),
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
});
export type ProductEventPayload = z.infer<typeof ProductEventPayloadSchema>;

/**
 * Készlet-változás — **aggregált**, nem darab-szintű.
 *
 * A belső `InventoryItem` darab-szintű (`serialNumber` van rajta); kifelé a
 * mennyiség és az elérhetőség tartozik. Az aggregált alak a `shared/inventory`
 * `availableQuantity` / `stockLevelStatus` mezőiből képződik, szűkítő mapperrel.
 */
export const StockEventPayloadSchema = z.object({
  productId: z.string().uuid(),
  availableQuantity: z.number().int().nonnegative(),
  status: PublicStockStatusSchema,
});
export type StockEventPayload = z.infer<typeof StockEventPayloadSchema>;

/**
 * Bérlés — a kifelé látható állapot.
 *
 * ⛔ **Nincs benne pénz**: a díj, a kaució és a késedelmi díj az ERP elszámolása
 * (ADR-145 D5 — a web igényt rögzít, az ERP elszámol). Az összeg-mezők kiadása
 * külön, auditált végponton menne, nem esemény-broadcastban.
 */
export const RentalEventPayloadSchema = z.object({
  rentalId: z.string().uuid(),
  partnerId: z.string().uuid(),
  status: PublicRentalStatusSchema,
  startDate: TimestampSchema,
  endDate: TimestampSchema.nullable(),
});
export type RentalEventPayload = z.infer<typeof RentalEventPayloadSchema>;

/**
 * A teljes esemény — boríték + a névhez tartozó payload.
 *
 * A diszkrimináció az `event` mezőn megy; a fogyasztó a `schemaVersion` alapján
 * dönt, ismeri-e az alakot.
 */
export const IntegrationEventSchema = z.discriminatedUnion('event', [
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('partner.created'),
    payload: PartnerEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('partner.updated'),
    payload: PartnerEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('product.created'),
    payload: ProductEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('product.updated'),
    payload: ProductEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('stock.changed'),
    payload: StockEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('rental.created'),
    payload: RentalEventPayloadSchema,
  }),
  IntegrationEventEnvelopeSchema.extend({
    event: z.literal('rental.status_changed'),
    payload: RentalEventPayloadSchema,
  }),
]);
export type IntegrationEvent = z.infer<typeof IntegrationEventSchema>;
